import React, { useState, useRef } from 'react';
import { Grid, TextField, Box, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { GiftProduct, GiftProductType } from '../../../../models';
import { AsyncAutocomplete, ImageWithFallback } from '../../../../components';

interface VirtualProductFormProps {
  product: Partial<GiftProduct>;
  onChange: (product: Partial<GiftProduct>) => void;
}

const VirtualProductForm: React.FC<VirtualProductFormProps> = ({ product, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleChange = (field: string, value: any) => {
    onChange({
      ...product,
      type: GiftProductType.Virtual,
      [field]: value,
    });
  };

  const handleFileChange = (file: File) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // IMPORTANTE: Atualizar AMBOS os campos em uma única chamada
        // para evitar perder o File devido a atualizações assíncronas do estado
        onChange({
          ...product,
          type: GiftProductType.Virtual,
          productImageFile: file,
          productImagePath: reader.result as string,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileChange(file);
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Nome do Produto"
            value={product.productName || ''}
            onChange={(e) => handleChange('productName', e.target.value)}
            required
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Descrição"
            value={product.productDescription || ''}
            onChange={(e) => handleChange('productDescription', e.target.value)}
          />
        </Grid>
        <Grid size={12}>
          <AsyncAutocomplete
            multiple
            resource="chains/chainId/sale-products"
            label="Produtos de Venda"
            value={product.saleProductIds || []}
            onChange={(newValue) => {
              // newValue pode conter objetos completos ou IDs primitivos
              // Normalizar para array de IDs
              const products = Array.isArray(newValue) ? newValue : [];
              const productIds = products.map((item: any) =>
                typeof item === 'string' || typeof item === 'number' ? item : item?.id
              ).filter(Boolean);
              handleChange('saleProductIds', productIds);
            }}
            getOptionLabel={(option: any) => option.name || option.id || ''}
            filters={{ isDisabled: false }}
            sort={{ field: 'name', order: 'ASC' }}
            helperText="Selecione os produtos de venda relacionados"
          />
        </Grid>
        <Grid size={12}>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Imagem do Produto
          </Typography>

          {/* Input escondido */}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileChange(file);
            }}
          />

          {/* Área de upload visual */}
          <Box
            onClick={handleClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              width: '100%',
              maxWidth: '100%',
              minHeight: 120,
              border: '2px dashed',
              borderColor: isDragging ? 'primary.main' : '#d0d0d0',
              borderRadius: 2,
              backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.04)' : '#fafafa',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              padding: 2,
              boxSizing: 'border-box',
              overflow: 'hidden',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            <CloudUploadIcon
              sx={{
                fontSize: 40,
                color: isDragging ? 'primary.main' : '#999',
                mb: 1,
              }}
            />
            <Typography
              variant="body2"
              color={isDragging ? 'primary' : 'text.secondary'}
              fontWeight={500}
              textAlign="center"
            >
              {product.productImagePath
                ? 'Clique para alterar ou arraste uma nova imagem'
                : 'Clique para selecionar ou arraste uma imagem'
              }
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Formatos aceitos: PNG, JPG, GIF
            </Typography>
          </Box>

          {/* Preview da imagem */}
          {product.productImagePath && (
            <Box mt={2} display="flex" justifyContent="center">
              <ImageWithFallback
                src={product.productImagePath}
                alt="Preview do produto"
                width={200}
                height={200}
                borderRadius={8}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default VirtualProductForm;
